/**
 * usePowerSavingTemplate.ts
 */
import Focus from '@shared/types/enums/focus';
import Storage from '@storage';
import TranslationManager from '@shared/assets/locale/translationsManager';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { popRoute } from '@shared/store/slices/router.slice';
import { setFocus } from '@shared/store/slices/focus.slice';
import { useAppDispatch } from '@shared/store/hooks';
import { useCallback, useEffect, useMemo } from 'react';

/**
 * Hook to use the power saving template.
 * Handles the power saving template logic.
 */
export default function usePowerSavingTemplate() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onMouseEnter = useCallback(() => {
    dispatch(setFocus(Focus.POWER_SAVING));
  }, []);

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    dispatch(setFocus(Focus.POWER_SAVING));
  }, []);

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  // Power saving keyboard
  useKeyboard({
    focus: Focus.POWER_SAVING,
    keyDownHandler: (key: string) => {
      switch (key) {
        case 'BACK':
          dispatch(popRoute());
          break;
        case 'LEFT':
          dispatch(setFocus(Focus.MENU));
          break;
      }
    },
  });

  // Menu keyboard
  useKeyboard({
    focus: Focus.MENU,
    keyDownHandler: (key: string) => {
      switch (key) {
        case 'RIGHT':
          dispatch(setFocus(Focus.POWER_SAVING));
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    return {
      callbacks: {
        container: {
          onMouseEnter,
        },
      },
      literals: {
        title: TranslationManager.get('PAGES.SETTINGS.POWER_SAVING.PAGE_TITLE'),
        description: TranslationManager.get(
          'PAGES.SETTINGS.POWER_SAVING.DESCRIPTION'
        ),
      },
      props: {
        switcher: {
          active:
            Storage.get('deadmanActive') === '1' ||
            !Storage.get('deadmanActive'),
        },
      },
    };
  }, [onMouseEnter]);
}
